import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { IMenuModel } from 'src/app/shared/components/properties-overview/models/IMenuModel';
import { getCommands, getProperties, getTelemetry } from 'src/app/shared/extensions/device-capability-model/device-capability-model.extensions';
import { ResetAssetDetails } from '../asset-details.actions';
import * as actions from './asset-capability.action';
import { AssetCapabilityStateModel, assetCapabilityDefaults } from './asset-capability.state.model';

@State<AssetCapabilityStateModel>(assetCapabilityDefaults)
@Injectable()
export class AssetCapabilityState {
    @Selector()
    public static getTelemetry(state: AssetCapabilityStateModel) {
        return getTelemetry(state.capabilityModelDefinition);
    }

    @Selector()
    public static getAssetCommands(state: AssetCapabilityStateModel) {
        return getCommands(state.capabilityModelDefinition);
    }

    @Selector()
    public static getProperties(state: AssetCapabilityStateModel) {
        return getProperties(state.capabilityModelDefinition);
    }

    @Selector()
    public static getAssetCapabilityModel(state: AssetCapabilityStateModel) {
        return state.uiCapabilityModel;
    }

    @Selector()
    public static getMenus(state: AssetCapabilityStateModel) {
        return state.menus;
    }

    @Selector()
    public static getDeviceType(state: AssetCapabilityStateModel) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const dcm: any = state.capabilityModelDefinition?.model;
        const id: string = dcm?.['@id'] ?? 'urn:stego:iolink';
        return id.split(':')?.[2] ?? 'iolink';
    }

    @Action(actions.GetDcmMenu)
    public getDcmMenu({ getState, setState }: StateContext<AssetCapabilityStateModel>, action: actions.GetDcmMenu) {
        const state = getState();
        const menusJson = state.uiCapabilityModel?.[action.roleMenu];
        if (menusJson == null) {
            setState({
                ...state,
                menus: null as IMenuModel[],
            });

            return;
        }

        const menus = this.createMenus(Object.values(menusJson) as IMenuModel[]);

        setState({
            ...state,
            menus,
        });
    }

    @Action(actions.SetCapabilityModel)
    public setCapabilityModel({ patchState }: StateContext<AssetCapabilityStateModel>, action: actions.SetCapabilityModel) {
        patchState({
            capabilityModelDefinition: action.capabilityModelDefinition,
        });
    }

    @Action(actions.SetCapabilityModelUi)
    public setCapabilityModelUi({ patchState }: StateContext<AssetCapabilityStateModel>, action: actions.SetCapabilityModelUi) {
        patchState({
            uiCapabilityModel: action.uiDcm,
        });
    }

    @Action(ResetAssetDetails)
    public resetState(ctx: StateContext<AssetCapabilityStateModel>) {
        ctx.setState(assetCapabilityDefaults.defaults);
    }

    private createMenus(menus: IMenuModel[], parent: IMenuModel = null) {
        const newMenus: IMenuModel[] = [];

        for (const menu of menus) {
            if (!menu.contents?.length && !menu.submenus?.length) {
                // ignore empty menus
                continue;
            }

            var newMenu = Object.assign({}, menu);
            newMenu.id = this.createMenuId(newMenu, parent);
            newMenu.parentId = parent?.id;

            if (newMenu.submenus.length) {
                newMenu.submenus = this.createMenus(newMenu.submenus, newMenu);
            }

            newMenus.push(newMenu);
        }

        return newMenus;
    }

    private createMenuId(menu: IMenuModel, parent: IMenuModel = null) {
        const regex = /[^A-Za-z0-9 ]/gi;
        const displayName = menu.displayName?.global ?? menu.displayName?.en;
        const id = displayName.replace(regex, '').replace(/\s+/gi, '-').toLowerCase();
        return (parent != null ? parent.id + '--' : '') + id;
    }
}
